import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import {
  formatSearchActivityElapsed,
  getDeterminateSearchActivityProgressPercentage,
  getSearchActivityElapsedMs,
  type SearchExecutionActivity,
} from "@/lib/search-activity";

const phaseConfig: Record<
  SearchExecutionActivity["phase"],
  { label: string; color: string }
> = {
  preparing: { label: "Preparing", color: "text-info" },
  queued: { label: "Queued", color: "text-info" },
  generating: { label: "Generating", color: "text-info" },
  scanning: { label: "Scanning", color: "text-info" },
  completed: { label: "Completed", color: "text-success" },
  failed: { label: "Failed", color: "text-destructive" },
  interrupted: { label: "Interrupted", color: "text-warning" },
};

/**
 * SVG overlay that draws a dashed rounded-rect border matching the full
 * border-box of the parent pill. Dashes animate when active.
 */
function DashedBorderOverlay({ active }: { active: boolean }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const parent = svgRef.current?.parentElement;
    if (!parent) return;

    const measure = () => {
      const { offsetWidth: w, offsetHeight: h } = parent;
      setBox((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };

    measure();
    const ro = new ResizeObserver(() => measure());
    ro.observe(parent);
    return () => ro.disconnect();
  }, []);

  if (box.w === 0) {
    return (
      <svg
        ref={svgRef}
        className="pointer-events-none absolute inset-0 h-full w-full"
        aria-hidden
      />
    );
  }

  const inset = 0.75;
  const rx = (box.h - inset * 2) / 2;

  return (
    <svg
      ref={svgRef}
      className="pointer-events-none absolute inset-0"
      width={box.w}
      height={box.h}
      viewBox={`0 0 ${box.w} ${box.h}`}
      aria-hidden
    >
      <rect
        x={inset}
        y={inset}
        width={box.w - inset * 2}
        height={box.h - inset * 2}
        rx={rx}
        ry={rx}
        fill="none"
        stroke="currentColor"
        strokeWidth="1"
        strokeDasharray="4 3"
        className={cn(
          "transition-opacity",
          active
            ? "opacity-45 animate-[dash-rotate_6s_linear_infinite]"
            : "opacity-25",
        )}
      />
    </svg>
  );
}

/**
 * Non-interactive status pill that sits alongside SearchMetadataPill and
 * SearchScheduler in the page header meta row.
 *
 * Matches SearchMetadataPill dimensions: text-xs, rounded-full, similar padding.
 * Uses a rotating dashed SVG border when the search is active.
 */
export function SearchActivityPill({
  activity,
}: {
  activity: SearchExecutionActivity | null | undefined;
}) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!activity) {
      return;
    }

    const interval = window.setInterval(
      () => setNow(Date.now()),
      activity.isActive ? 1000 : 60_000,
    );
    return () => window.clearInterval(interval);
  }, [activity]);

  if (!activity) return null;

  const { label, color } = phaseConfig[activity.phase];
  const elapsed = formatSearchActivityElapsed(
    getSearchActivityElapsedMs(activity, now),
  );
  const pct = getDeterminateSearchActivityProgressPercentage(activity);
  const isActive = activity.isActive;
  const hasDeterminateProgress = pct !== null;

  return (
    <div
      className={cn(
        "relative flex w-fit select-none items-center gap-1.5 rounded-full px-3 py-1.5",
        color,
      )}
    >
      <DashedBorderOverlay active={isActive} />

      {/* Dot */}
      <span className="relative flex h-1.5 w-1.5 shrink-0">
        {isActive && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-50" />
        )}
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-current" />
      </span>

      {/* Label */}
      <span className="text-xs font-medium leading-none">{label}</span>

      {/* Value: percentage when generating, elapsed otherwise */}
      {hasDeterminateProgress && isActive ? (
        <span className="text-xs tabular-nums leading-none opacity-60">
          {pct}%
        </span>
      ) : (
        <span className="text-xs tabular-nums leading-none opacity-60">
          {elapsed}
        </span>
      )}
    </div>
  );
}
